import { useEffect, useState } from 'react';
import { repeat, tap } from 'rxjs';
import { format } from 'date-fns';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler';
import { localeFormatterHelper } from '../../../../../../shared/helpers/formatter/localeFormatter.helper';
import {
  WindowsGeneratedReportPreviewRes,
  IArticlesReportParams,
  IOfferReportParams,
  IOrderReportParams,
  IInvoiceReportParams,
  IDebtReminderReportParams,
  IGetR10NotifierFileRes,
  IRecheckReportTaskParams,
  reportsService,
} from '../../../../../../shared/services/reports/reports.service';
import { C_Report, C_Win_Report_Task_Status } from '../../../../../../graphql/generatedModel';
import { IGeneratedReportData } from './components/reportPreviewArea';

/*
A polling budget that ran out used to arrive here as a thrown
`reports.taskStatusPollingMaxAttemptsExceeded`, which this hook turned into
`production.request_error_message` + a Sentry report — i.e. a failure notice and an auto-filed
Jira ticket for a report that was merely slow and landed in S3 seconds later (WA-3969, Sentry
WAWIASSIST-25C). `_taskStatusPolling` now ends such a run on the last status it saw instead of
throwing, so it reaches us as a normal value whose status is WRTS1/WRTS5 and the preview area
renders its "still being built" state. What is left for the error handler is genuine failure,
which keeps the default message and the Sentry report.
*/
const createReportResponseHandler = <T>() =>
  responseHandler<T>({
    errorReturnType: {} as unknown as T,
  });

const reminderPreviewStatus = (res: IGetR10NotifierFileRes): C_Win_Report_Task_Status => {
  if (res.stillBuilding) return C_Win_Report_Task_Status.WRTS5_IN_PROGRESS;
  if (res.noData) return C_Win_Report_Task_Status.WRTS15_NO_DATA_FOR_REPORT;
  return C_Win_Report_Task_Status.WRTS2_READY;
};

export const useReportPreview = (): IUseReportPreview => {
  const [loading, setLoading] = useState<boolean>(false);
  const [generatedReport, setGeneratedReport] = useState<IUseReportPreview['generatedReport']>({});

  useEffect(() => {
    const unsubArticlePricelistReport = reportsService.sub
      .createArticlesReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubOfferReport = reportsService.sub
      .createOfferReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubOrderReport = reportsService.sub
      .createOrderReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubInvoiceReport = reportsService.sub
      .createInvoiceReport()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    const unsubDebtReminderReport = reportsService.sub
      .createDebtReminderReport()
      .pipe(
        createReportResponseHandler<IGetR10NotifierFileRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe((v) => {
        // The R10 result carries no task status of its own, only the two flags the service
        // sets for the outcomes that are not failures; map them onto the status this page
        // renders. Until WA-3969 every outcome was reported as READY, so a reminder with no
        // data (WA-3868) and one that was still being built both ended up as the blank stub.
        setGeneratedReport({
          taskId: v.taskId,
          s3Key: v.s3Key ?? '',
          url: v.url ?? '',
          status: reminderPreviewStatus(v),
        });
      });

    const unsubRecheckReportTask = reportsService.sub
      .recheckReportTask()
      .pipe(
        createReportResponseHandler<WindowsGeneratedReportPreviewRes>(),
        tap(() => setLoading(false)),
        repeat(),
      )
      .subscribe(setGeneratedReport);

    return () => {
      unsubArticlePricelistReport.unsubscribe();
      unsubOfferReport.unsubscribe();
      unsubOrderReport.unsubscribe();
      unsubInvoiceReport.unsubscribe();
      unsubDebtReminderReport.unsubscribe();
      unsubRecheckReportTask.unsubscribe();
    };
  }, []);

  const resetGeneratedReport = () => {
    setGeneratedReport({});
  };

  const generateArticlePricelist: IUseReportPreview['generateArticlePricelist'] = ({
    articlesIds,
    priceValidOn,
    customerId,
  }) => {
    const values: IArticlesReportParams = {
      articlesIds,
      priceValidOn,
      reportId: C_Report.R6_ARTICLE_LIST,
      priceMode: 'byCustomer',
      customerId,
    };
    setLoading(true);
    reportsService.pub.createArticlesReport(values);
  };

  const generateOffer: IUseReportPreview['generateOffer'] = ({ reportId, offerId }) => {
    const values: IOfferReportParams = {
      reportId,
      offerId,
    };
    setLoading(true);
    reportsService.pub.createOfferReport(values);
  };

  const generateDeliveryNote: IUseReportPreview['generateDeliveryNote'] = ({
    reportId,
    orderId,
  }) => {
    const values: IOrderReportParams = {
      reportId,
      orderId,
    };
    setLoading(true);
    reportsService.pub.createOrderReport(values);
  };

  const generateInvoice: IUseReportPreview['generateInvoice'] = ({ invoiceId }) => {
    const values: IInvoiceReportParams = {
      reportId: C_Report.R9_INVOICE_A4,
      invoiceId,
    };
    setLoading(true);
    reportsService.pub.createInvoiceReport(values);
  };

  const generatePaymentSlip: IUseReportPreview['generatePaymentSlip'] = ({ invoiceId }) => {
    const values: IInvoiceReportParams = {
      reportId: C_Report.R59_INVOICE_PAYMENTSLIP_WITH_QR,
      invoiceId,
    };
    setLoading(true);
    reportsService.pub.createInvoiceReport(values);
  };

  const generateDebtReminder: IUseReportPreview['generateDebtReminder'] = ({
    invoiceId,
    invoiceDate,
    customerId,
  }) => {
    const dateForReport = new Date(invoiceDate);
    const considerPaymentsTill = new Date(invoiceDate);
    considerPaymentsTill.setDate(considerPaymentsTill.getDate() - 1);
    const values = {
      invoicesList: [invoiceId],
      customerId,
      periodStart: format(localeFormatterHelper.localizedDate(dateForReport), 'yyyy-MM-dd'),
      periodEnd: format(localeFormatterHelper.localizedDate(dateForReport), 'yyyy-MM-dd'),
      paymentsTill: format(localeFormatterHelper.localizedDate(considerPaymentsTill), 'yyyy-MM-dd'),
    };
    setLoading(true);
    reportsService.pub.createDebtReminderReport({ params: values });
  };

  const recheckReport: IUseReportPreview['recheckReport'] = (params) => {
    setLoading(true);
    reportsService.pub.recheckReportTask(params);
  };

  return {
    loading,
    generatedReport,
    resetGeneratedReport,
    recheckReport,
    generateArticlePricelist,
    generateOffer,
    generateDeliveryNote,
    generateInvoice,
    generatePaymentSlip,
    generateDebtReminder,
  };
};

interface IUseReportPreview {
  loading: boolean;
  generatedReport: IGeneratedReportData;
  resetGeneratedReport: () => void;
  /** Re-polls a report that was already requested, instead of generating it again (WA-3969). */
  recheckReport: (params: IRecheckReportTaskParams) => void;
  generateArticlePricelist: (params: IArticlePricelistParams) => void;
  generateOffer: (params: IOfferReportParams) => void;
  generateDeliveryNote: (params: IOrderReportParams) => void;
  generateInvoice: (params: IInvoiceA4Params) => void;
  generatePaymentSlip: (params: IPaymentSlipParams) => void;
  generateDebtReminder: (params: IDebtReminderParams) => void;
}

type IArticlePricelistParams = Omit<
  IArticlesReportParams,
  'reportId' | 'priceMode' | 'priceCategory'
>;

type IInvoiceA4Params = Omit<IInvoiceReportParams, 'reportId'>;
type IPaymentSlipParams = Omit<IInvoiceReportParams, 'reportId'>;

type IDebtReminderParams = Omit<IDebtReminderReportParams, 'reportId'>;
